import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import { provideDateFnsAdapter } from "@angular/material-date-fns-adapter";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { NoPreloading, provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading, withRouterConfig } from "@angular/router";
import { EXTJS_CONFIG } from "@core/configs/extjs.config";
import { HTTP_CONFIG } from "@core/configs/http-configs/http.config";
import { KEYCLOAK_CONFIG } from "@core/configs/keycloak.config";
import { TOAST_CONFIG, ToastRegistry } from "@core/configs/toast/toast.config";
import { TRANSLATE_CONFIG } from "@core/configs/translation/translate.config";
import { CORE_ROUTES } from "@core/constants/core-routes.constant";
import { ServerErrorInterceptor } from "@core/interceptors/server-error.interceptor";
import { waitCursorInterceptor } from "@core/interceptors/wait-cursor.interceptor";
import { IFRAME_URL } from "@core/services/extjs/extjs.tokens";
import { HttpService } from "@core/services/http/http.service";
import { provideToastConfig } from "@core/types/toast/toast-config.types";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { provideHttpConfig } from "@ogs-gmbh/ngx-http";
import { provideTemplateEngineConfig } from "@ogs-gmbh/ngx-template-engine";
import { TranslationModule } from "@ogs-gmbh/ngx-translate";
import { KeycloakInterceptor, KeycloakModule } from "@ogs/ngx-keycloak";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { provideTableTheme } from "@shared/components/table/types/table-theme.type";
import { de } from "date-fns/locale";
import localeDe from "@angular/common/locales/de";
import localeEn from "@angular/common/locales/en";
import localeFr from "@angular/common/locales/fr";
import "@ogs-gmbh/stdx";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from "@angular/material/tooltip";
import { registerLocaleData } from "@angular/common";
import { provideOperatorConfig } from "@ogs-gmbh/rsql";
import { RSQL_OPERATORS } from "@core/configs/rsql/rsql-operator.config";
import { provideEnvironmentNgxMask } from "ngx-mask";
import { DialogService } from "@core/services/dialog.service";


// Register locales for Angular i18n (e.g. for date and currency pipes)
registerLocaleData(localeDe, "de");
registerLocaleData(localeEn, "en");
registerLocaleData(localeFr, "fr");
// Provide PLS specific overrides for the rsql operators
provideOperatorConfig(RSQL_OPERATORS);

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      KeycloakModule.forRoot(KEYCLOAK_CONFIG),
      IconModule.forRoot({ prefix: "fal" }),
      TranslationModule.forRoot(TRANSLATE_CONFIG)
    ),
    FontAwesomeModule,
    provideAppInitializer(() => {
      inject(DialogService);
    }),
    provideRouter(CORE_ROUTES,
      withInMemoryScrolling({ scrollPositionRestoration: "top" }),
      withRouterConfig({ onSameUrlNavigation: "ignore" }),
      withPreloading(NoPreloading),
      withComponentInputBinding()),

    provideHttpConfig(HTTP_CONFIG),
    {
      provide: IFRAME_URL,
      useValue: EXTJS_CONFIG.url
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: "outline",
        subscriptSizing: "dynamic"
      }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 400,
        hideDelay: 700
      } satisfies Partial<MatTooltipDefaultOptions>
    },
    HttpService,
    { provide: MAT_DATE_LOCALE, useValue: de },
    provideDateFnsAdapter({
      parse: { dateInput: [ "P", "PP" ] },
      display: { dateInput: "P", monthYearLabel: "MMM yyyy", dateA11yLabel: "PP", monthYearA11yLabel: "LLLL yyyy" }
    }),
    provideEnvironmentNgxMask({
      triggerOnMaskChange: false
    }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([ waitCursorInterceptor ])
    ),
    KeycloakInterceptor,
    provideToastConfig<ToastRegistry>(TOAST_CONFIG),
    provideTemplateEngineConfig({ fallbackOnError: true }),
    provideTableTheme()
  ]
};
